import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { StatsModel } from "../../api/stats/StatsModel";
import { StatsAdapter } from "../../api/stats/StatsAdapter";
import type {
  StatDate,
  ExcursionsStats,
  PeriodStats,
  TrainingStats,
  StatsHandler,
  InstructorStat,
  StatsDateRange,
  StatsGranularity,
} from "../../api/stats/Stats.typed";

interface StatsContextType {
  getStatDates: () => Promise<StatDate[] | undefined>;
  getExcursionsStatsByYear: (date: StatsHandler) => Promise<ExcursionsStats[] | undefined>;
  getExcursionsStatsByMonth: (date: StatsHandler) => Promise<ExcursionsStats | undefined>;
  getPeriodStats: (
    granularity: StatsGranularity,
    range: StatsDateRange,
  ) => Promise<PeriodStats[] | undefined>;
  getTrainingStatsByYear: (date: StatsHandler) => Promise<TrainingStats[] | undefined>;
  getInstructorStats: (
    range: StatsDateRange,
    instructorId?: number,
  ) => Promise<InstructorStat[] | undefined>;
}

const StatsContext = React.createContext<StatsContextType>(null!);

export function StatsProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new StatsModel(appConfig);
    return new StatsAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getStatDates = React.useCallback(
    async (): Promise<StatDate[] | undefined> =>
      adapter ? wrap(() => adapter.getStatDates()) : undefined,
    [adapter, wrap],
  );

  const getExcursionsStatsByYear = React.useCallback(
    async (date: StatsHandler): Promise<ExcursionsStats[] | undefined> =>
      adapter ? wrap(() => adapter.getExcursionsStatsByYear(date)) : undefined,
    [adapter, wrap],
  );

  const getExcursionsStatsByMonth = React.useCallback(
    async (date: StatsHandler): Promise<ExcursionsStats | undefined> =>
      adapter ? wrap(() => adapter.getExcursionsStatsByMonth(date)) : undefined,
    [adapter, wrap],
  );

  const getPeriodStats = React.useCallback(
    async (
      granularity: StatsGranularity,
      range: StatsDateRange,
    ): Promise<PeriodStats[] | undefined> =>
      adapter ? wrap(() => adapter.getPeriodStats(granularity, range)) : undefined,
    [adapter, wrap],
  );

  const getTrainingStatsByYear = React.useCallback(
    async (date: StatsHandler): Promise<TrainingStats[] | undefined> =>
      adapter ? wrap(() => adapter.getTrainingStatsByYear(date)) : undefined,
    [adapter, wrap],
  );

  const getInstructorStats = React.useCallback(
    async (range: StatsDateRange, instructorId?: number): Promise<InstructorStat[] | undefined> =>
      adapter ? wrap(() => adapter.getInstructorStats(range, instructorId)) : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<StatsContextType>(
    () => ({
      getStatDates,
      getExcursionsStatsByYear,
      getExcursionsStatsByMonth,
      getPeriodStats,
      getTrainingStatsByYear,
      getInstructorStats,
    }),
    [
      getStatDates,
      getExcursionsStatsByYear,
      getExcursionsStatsByMonth,
      getPeriodStats,
      getTrainingStatsByYear,
      getInstructorStats,
    ],
  );

  return <StatsContext.Provider value={value}>{props.children}</StatsContext.Provider>;
}

export function useStats(): StatsContextType {
  const ctx = React.useContext(StatsContext);
  if (!ctx) throw new Error("useStats must be used within StatsProvider");
  return ctx;
}
